/**
 * Cumulative projection utility — Damped Holt exponential smoothing + N-1 seasonality blend.
 *
 * Based on Hyndman (fpp3), Bates & Granger (1969) forecast combination research.
 * Works on plain number[] arrays — no domain-specific types.
 */

export interface ProjectionInput {
  /** Full period cumulative values (untrimmed) */
  currentCumulative: number[];
  /** N-1 year cumulative values, same length as currentCumulative */
  n1Cumulative?: number[];
}

export interface ProjectionResult {
  /** Index where real data ends (first index of the trailing plateau) */
  cutoff: number;
  /** Sparse array (full length): nulls before cutoff-1, projected values from cutoff-1 onward */
  projected: (number | null)[];
  /** Final projected value */
  projectedEnd: number;
}

/**
 * Find cutoff by detecting trailing plateau in cumulative values.
 * Walk backwards from end; when values stop being equal, that index is the cutoff.
 */
export function trimToPresent(values: number[]): number {
  if (values.length <= 1) return values.length;
  let cutoff = values.length;
  for (let i = values.length - 1; i > 0; i--) {
    if (values[i] !== values[i - 1]) break;
    cutoff = i;
  }
  return cutoff;
}

/**
 * Compute a blended projection combining Damped Holt exponential smoothing
 * with N-1 proportional completion (seasonality).
 *
 * Returns null if cutoff <= 1 or there are no remaining intervals to project.
 */
export function computeProjection(input: ProjectionInput): ProjectionResult | null {
  const { currentCumulative, n1Cumulative } = input;
  const totalLen = currentCumulative.length;
  const cutoff = trimToPresent(currentCumulative);
  const remainingCount = totalLen - cutoff;

  if (remainingCount <= 0 || cutoff <= 1) return null;

  const lastReal = currentCumulative[cutoff - 1];

  // --- Damped Holt exponential smoothing ---
  const increments: number[] = [];
  for (let i = 1; i < cutoff; i++) {
    increments.push(currentCumulative[i] - currentCumulative[i - 1]);
  }

  const alpha = 0.3;  // level smoothing
  const beta = 0.05;  // trend smoothing (low for stability)
  const phi = 0.90;   // damping factor

  // OLS regression initialization (Hyndman recommended)
  const n = increments.length;
  const xMean = (n - 1) / 2;
  const yMean = increments.reduce((s, v) => s + v, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (i - xMean) * (increments[i] - yMean);
    den += (i - xMean) * (i - xMean);
  }
  let trend = den > 0 ? num / den : 0;
  let level = yMean - trend * xMean;

  // Run Holt smoothing
  for (let i = 1; i < n; i++) {
    const prevLevel = level;
    level = alpha * increments[i] + (1 - alpha) * (level + phi * trend);
    trend = beta * (level - prevLevel) + (1 - beta) * phi * trend;
  }

  // Project with damped trend: phi + phi^2 + ... + phi^h
  const holtProj: number[] = [lastReal];
  let phiSum = 0;
  for (let i = 1; i <= remainingCount; i++) {
    phiSum += Math.pow(phi, i);
    const forecastIncrement = Math.max(0, level + trend * phiSum);
    holtProj.push(Math.round(holtProj[i - 1] + forecastIncrement));
  }

  // --- N-1 proportional completion projection (carries seasonality) ---
  const hasN1Data = n1Cumulative && n1Cumulative.length >= totalLen;
  let n1Proj: number[] | null = null;

  if (hasN1Data) {
    const n1AtCutoff = n1Cumulative![cutoff - 1];
    const n1Final = n1Cumulative![totalLen - 1];

    if (n1Final > 0 && n1AtCutoff > 0) {
      const completion = n1AtCutoff / n1Final;
      const projectedEnd = Math.round(lastReal / completion);
      const remainingGrowth = projectedEnd - lastReal;
      const n1RemainingGrowth = n1Final - n1AtCutoff;

      n1Proj = [lastReal];

      if (n1RemainingGrowth > 0 && remainingGrowth > 0) {
        for (let i = 1; i <= remainingCount; i++) {
          const n1FutureVal = n1Cumulative![cutoff - 1 + i] ?? n1Final;
          const n1FractionalProgress = Math.max(0, Math.min(1,
            (n1FutureVal - n1AtCutoff) / n1RemainingGrowth
          ));
          n1Proj.push(Math.round(lastReal + remainingGrowth * n1FractionalProgress));
        }
      } else {
        for (let i = 1; i <= remainingCount; i++) n1Proj.push(lastReal);
      }
    }
  }

  // --- Dynamic blend: N-1 dominates early, Holt trend dominates late ---
  const progressRatio = cutoff / totalLen;
  const W_N1 = 0.8 - 0.6 * progressRatio;   // ramps 0.8 -> 0.2
  const W_HOLT = 1 - W_N1;                    // ramps 0.2 -> 0.8

  const blended: number[] = [lastReal];
  let runningMax = lastReal;
  for (let i = 1; i <= remainingCount; i++) {
    let val: number;
    if (n1Proj) {
      val = Math.round(W_N1 * n1Proj[i] + W_HOLT * holtProj[i]);
    } else {
      val = holtProj[i];
    }
    runningMax = Math.max(runningMax, val);
    blended.push(runningMax);
  }

  // Build sparse array for chart
  const proj: (number | null)[] = Array(totalLen).fill(null);
  for (let i = 0; i <= remainingCount; i++) {
    proj[cutoff - 1 + i] = blended[i];
  }

  return {
    cutoff,
    projected: proj,
    projectedEnd: proj[totalLen - 1] as number,
  };
}
